import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'

import {
  type HeadItemType,
  INITIAL_PAGE,
  ITEMS_PER_PAGE,
  LoadingHavaianas,
  Pagination,
  type PaginationParams,
  Table,
} from 'components'
import { ModalContentLayout } from 'layouts/ModalContentLayout'
import { toast } from 'react-toastify'
import { getKeyAccountTracking } from 'services/keyAccounts'
import type { KeyAccountTracking } from 'services/keyAccounts/types'

import * as S from './styles'

interface TrackingModalProps {
  setOpen: Dispatch<SetStateAction<boolean>>
}

export const TrackingModal = ({ setOpen }: TrackingModalProps) => {
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState<PaginationParams>({
    currentPage: INITIAL_PAGE,
    totalItems: 0,
  })
  const [trackingData, setTrackingData] = useState<KeyAccountTracking[]>([])

  const tableLabels: HeadItemType[] = [
    { columnName: 'date', label: 'Data' },
    { columnName: 'time', label: 'Hora' },
    { columnName: 'user', label: 'Usuário' },
    { columnName: 'name', label: 'Cliente' },
    { columnName: 'id', label: 'Código SAP' },
    { columnName: 'cnpj', label: 'CNPJ' },
    { columnName: 'action', label: 'Ação' },
  ]

  const fetchTrackingData = async (page: number) => {
    setLoading(true)

    try {
      const { pagination, data } = await getKeyAccountTracking({
        page,
        perPage: ITEMS_PER_PAGE,
      })

      setPagination(pagination)
      setTrackingData(data)
    } catch (error) {
      toast.error('Erro ao carregar tracking. Tente novamente mais tarde.')

      setOpen(false)
      setPagination({ currentPage: INITIAL_PAGE, totalItems: 0 })
      setTrackingData([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTrackingData(pagination.currentPage)
  }, [pagination.currentPage])

  const tableData = () => {
    if (!trackingData.length) return []

    return trackingData.map((tracking) => {
      return {
        content: [
          tracking.dateFormatted,
          tracking.timeFormatted,
          tracking.user,
          tracking.name,
          tracking.idSapCustomer,
          tracking.cnpjFormatted,
          tracking.action,
        ],
        id: tracking.id,
      }
    })
  }

  const handleClose = () => {
    setPagination({ ...pagination, currentPage: INITIAL_PAGE })
    setOpen(false)
  }

  const handlePageChange = (page: number) => {
    setPagination({
      ...pagination,
      currentPage: page,
    })
  }

  return (
    <ModalContentLayout
      closeAction={handleClose}
      closeButton
      description="Acompanhe o histórico completo de alterações realizadas."
      title="Tracking"
      titleSize={22}
      width={'95vw'}
    >
      {loading ? (
        <S.LoadingWrapper>
          <LoadingHavaianas />
        </S.LoadingWrapper>
      ) : (
        <>
          <Table
            bodyData={tableData()}
            fixedHead
            headData={tableLabels}
            maxHeight={667}
            rowHeight={48}
          />
          <Pagination
            currentPage={pagination?.currentPage}
            itemsPerPage={ITEMS_PER_PAGE}
            numberOfItems={pagination?.totalItems}
            setCurrentPage={handlePageChange}
          />
        </>
      )}
    </ModalContentLayout>
  )
}
